/**
 * @description: 全局注册组件
 */
import { defineAsyncComponent } from 'vue';
import '@/assets/style/index.scss';
import './assets/iconfont/iconfont.js';
import logger from '@/utils/logger.js';
import slsLogger from './utils/slsLogger/index.js';
import { Performance } from './utils/slsLogger/performance.js';
import { isBuild } from './jmHttp/host.js';
import i18n from '@/plugins/i18n';
import { getStorageAuthInfo } from '@/utils/storageOperateApi';
import { LOCAL_STORAGE_KEY } from '@/constants/index';
import { isOverseasEnv } from '@/utils/api';

import directives from './common/directive';
import Bridge from '@/thirdparty/scripts/bridge';
import Waf from '@/plugins/waf';

import Note from '@/pages/common/note/index.js';

const CustomDropdown = defineAsyncComponent(() => import('@/components/customDropdown'));
const CustomDropdownMultiple = defineAsyncComponent(() => import('@/components/customDropdownMultiple'));
const CustomDateRangePicker = defineAsyncComponent(() => import('@/components/customDateRangePicker'));
const ConfirmDialog = defineAsyncComponent(() => import('@/components/confirmDialog'));
const CustomInput = defineAsyncComponent(() => import('@/components/customInput'));
const CustomImage = defineAsyncComponent(() => import('@/components/customImage'));
const CustomScrollbar = defineAsyncComponent(() => import('@/components/customScrollbar'));
const CustomDrawer = defineAsyncComponent(() => import('@/components/customDrawer'));
const CustomSelectTag = defineAsyncComponent(() => import('@/components/customSelectTag'));
const CustomEmpty = defineAsyncComponent(() => import('@/components/customEmpty'));
const CustomLoadMore = defineAsyncComponent(() => import('@/components/customLoadMore'));
const CustomTabs = defineAsyncComponent(() => import('@/components/customTabs'));
const CustomTreeSelectTag = defineAsyncComponent(() => import('@/components/customTreeSelectTag'));
const CustomFilterPanel = defineAsyncComponent(() => import('@/components/customFilterPanel/index'));
const CustomAutocomplete = defineAsyncComponent(() => import('@/components/customAutocomplete'));
const CustomSelectAutocomplete = defineAsyncComponent(() => import('@/components/customSelectAutocomplete'));
const CustomSelectInput = defineAsyncComponent(() => import('@/components/customSelectInput'));
const CustomAffix = defineAsyncComponent(() => import('@/components/customAffix'));
const CustomEllipsis = defineAsyncComponent(() => import('@/components/customEllipsis'));
const CustomSelect = defineAsyncComponent(() => import('@/components/customSelect'));
const CustomTagsItem = defineAsyncComponent(() => import('@/components/customTagsItem'));
const CustomTextWithTooltip = defineAsyncComponent(() => import('@/components/customTextWithTooltip'));
const BusinessTag = defineAsyncComponent(() => import('@/components/businessTag.vue'));
const CustomBacktop = defineAsyncComponent(() => import('@/components/customBacktop'));
const CustomSvgLoader = defineAsyncComponent(() => import('@/components/customSvgLoader.vue'));
const CustomStocksPopover = defineAsyncComponent(() => import('@/components/customStocksPopover'));
const CustomMultipleDropdown = defineAsyncComponent(() => import('@/components/customMultipleDropdown'));
const CustomAiChat = defineAsyncComponent(() => import('@/components/customAiChat/index'));
// 插件
import Emitter from '@/plugins/emitter';

import {
  ArrowDown,
  ArrowDownBold,
  ArrowLeft,
  ArrowLeftBold,
  ArrowRight,
  ArrowRightBold,
  ArrowUp,
  CaretBottom,
  CaretTop,
  Check,
  CircleCheck,
  CircleCloseFilled,
  Close,
  CloseBold,
  Connection,
  Delete,
  Download,
  Loading,
  Lock,
  Message,
  Plus,
  Refresh,
  Search,
  Setting,
  Upload,
  View,
  Warning,
  WarningFilled
} from '@element-plus/icons-vue';
import ElementPlus from 'element-plus';
// 自定义按钮 延续element ui按钮使用习惯
import CustomButton from '@/components/customButton';

export default function install(app) {
  // 自定义指令
  app.use(directives);
  app.use(Emitter);
  // 桥接 用于合作机构
  app.use(Bridge);
  // WAF 反爬虫保护
  app.use(Waf);

  app.use(ElementPlus);

  app.use(Note);

  /**
   * 注册实际用到的图标（按需，而非全量 293 个）
   */
  const USED_ICONS = {
    ArrowDown,
    ArrowDownBold,
    ArrowLeft,
    ArrowLeftBold,
    ArrowRight,
    ArrowRightBold,
    ArrowUp,
    CaretBottom,
    CaretTop,
    Check,
    CircleCheck,
    CircleCloseFilled,
    Close,
    CloseBold,
    Connection,
    Delete,
    Download,
    Loading,
    Lock,
    Message,
    Plus,
    Refresh,
    Search,
    Setting,
    Upload,
    View,
    Warning,
    WarningFilled
  };
  for (const [key, component] of Object.entries(USED_ICONS)) {
    app.component(key, component);
  }

  app.component('CustomDropdown', CustomDropdown);
  app.component('CustomDropdownMultiple', CustomDropdownMultiple);
  app.component('CustomDateRangePicker', CustomDateRangePicker);
  app.component('CustomButton', CustomButton);
  app.component('ConfirmDialog', ConfirmDialog);
  app.component('CustomInput', CustomInput);
  app.component('CustomImage', CustomImage);
  app.component('CustomScrollbar', CustomScrollbar);
  app.component('CustomDrawer', CustomDrawer);
  app.component('CustomSelectTag', CustomSelectTag);
  app.component('CustomEmpty', CustomEmpty);
  app.component('CustomLoadMore', CustomLoadMore);
  app.component('CustomTabs', CustomTabs);
  app.component('CustomTreeSelectTag', CustomTreeSelectTag);
  app.component('CustomFilterPanel', CustomFilterPanel);
  app.component('CustomAutocomplete', CustomAutocomplete);
  app.component('CustomSelectAutocomplete', CustomSelectAutocomplete);
  app.component('CustomSelectInput', CustomSelectInput);
  app.component('CustomAffix', CustomAffix);
  app.component('CustomEllipsis', CustomEllipsis);
  app.component('CustomSelect', CustomSelect);
  app.component('CustomTagsItem', CustomTagsItem);
  app.component('CustomTextWithTooltip', CustomTextWithTooltip);
  app.component('BusinessTag', BusinessTag);
  app.component('CustomBacktop', CustomBacktop);
  app.component('CustomSvgLoader', CustomSvgLoader);
  app.component('CustomStocksPopover', CustomStocksPopover);
  app.component('CustomMultipleDropdown', CustomMultipleDropdown);
  app.component('CustomAiChat', CustomAiChat);

  window.logger = logger;
  window.$bridge = Bridge.getCurrentBridge();

  /**
   * 初始化日志系统
   */
  slsLogger.install(app, {
    isBuild: isBuild,
    i18n,
    logstore: isOverseasEnv() ? 'sea-finenter-workspace' : 'brm',
    getUserInfo: () => {
      return getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO, true) || {};
    }
  });

  Performance.init();
}
